import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './TutorialDevPage.module.css';
import { TUTORIAL_SPECS } from './TutorialSpecs';
import { TUTORIAL_TITLES, TUTORIAL_DESCRIPTIONS } from './TutorialTitles';
import { CONSTELLATION_DATA } from './ConstellationData';
import GlobalHeader from '../GlobalHeader';
import { playUISound } from '../../utils/UISoundEffects';

// Static tutorial development notes
const TUTORIAL_NOTES: { [constellation: string]: { [tutorialIndex: number]: string } } = {
  'Bear': {
    0: 'Reduced to 2 turn, simple and effective',
    1: 'Short and long pathing + basic movement - pass',
    2: 'Way too long winded? Could maybe be a tutorial that just needs to make sure the enemy does not race within a time frame?',
    3: 'Works quite well - easy to Race victory which exemplfies the effectiveness of \'Predator\' and capturing the enemy king for secret is still pretty easy but teaches basic Goat movement logic',
    4: 'Unsure - Seems too long, not puzzle focused but kind of fun? Unsure....'
  },
  'Crane': {
    0: 'I like the trapping / blocking and race focus\nI don\'t like how long the tutorial is\nI don\'t really like that the regular victory is just a slower secret victory',
    1: 'Too long as well, it would be much nicer if this tutorial was ~3 moves with a simple win option and a secret win option - maybe there is a king capture version of this were the regular victory is a medium difficulty puzzle and the king capture is a high difficulty puzzle'
  }
};

// Tutorials to hide from the dev page (already complete and verified)
const HIDDEN_TUTORIALS: { [constellation: string]: number[] } = {
  'Bear': [0, 1], // "How to move" and "How to race" are complete
};

// Tutorial completeness status based on analysis
// Red: Incomplete, Yellow: Unsure/Needs Review, Green: Complete
const TUTORIAL_STATUS: { [constellation: string]: { [tutorialIndex: number]: 'red' | 'yellow' | 'green' } } = {
  'Bear': { 0: 'green', 1: 'green', 2: 'red', 3: 'green', 4: 'yellow' },
  'Crane': { 0: 'yellow', 1: 'yellow', 2: 'green' },
  'Frog': { 0: 'red', 1: 'red', 2: 'red', 3: 'red', 4: 'red' },
  'Goat': { 0: 'red', 1: 'red', 2: 'red', 3: 'red' },
  'Monkey': { 0: 'green', 1: 'green', 2: 'green', 3: 'red', 4: 'green' },
  'Fungi': { 0: 'red', 1: 'red', 2: 'red', 3: 'red' },
  'Octopus': { 0: 'red', 1: 'red', 2: 'red', 3: 'red', 4: 'red', 5: 'red' },
  'Virus': { 0: 'red', 1: 'red', 2: 'red' },
  'Penguin': { 0: 'red', 1: 'red', 2: 'red', 3: 'red' },
  'Rhino': { 0: 'green', 1: 'red', 2: 'red', 3: 'red', 4: 'red' },
  'Snake': { 0: 'red', 1: 'red', 2: 'red', 3: 'red' },
  'Tree': { 0: 'green', 1: 'green', 2: 'green' },
  'Wolf': { 0: 'green', 1: 'green', 2: 'red' }
};

function TutorialDevPage() {
  const navigate = useNavigate();

  const getTutorialDescription = (constellation: string, index: number): string => {
    const descriptions = TUTORIAL_DESCRIPTIONS[constellation];
    if (!descriptions || index < 0 || index >= descriptions.length) {
      return 'No description available for this tutorial.';
    }
    return descriptions[index];
  };

  const handleTutorialClick = (constellation: string, tutorialIndex: number) => {
    navigate(`/tutorials/${constellation}/${tutorialIndex + 1}`);
  };

  const getStatusColor = (constellation: string, index: number): string => {
    return TUTORIAL_STATUS[constellation]?.[index] || 'red';
  };

  const getStatusClassName = (status: string): string => {
    if (status === 'green') return styles.statusGreen;
    if (status === 'yellow') return styles.statusYellow;
    return styles.statusRed;
  };

  const getTutorialNote = (constellation: string, index: number): string => {
    return TUTORIAL_NOTES[constellation]?.[index] || '';
  };

  return (
    <div className={styles.container}>
      <GlobalHeader />

      {/* Back button */}
      <button
        className={styles.homeBackButton}
        onClick={() => { playUISound(); navigate('/game'); }}
        aria-label="Back to game"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
      </button>

      <div className={styles.content}>
        <h1 className={styles.title}>Tutorial Development Page</h1>

        {/* Legend */}
        <div className={styles.legend}>
          <div className={styles.legendItem}>
            <div className={`${styles.legendColor} ${styles.statusGreen}`}></div>
            <span>Complete</span>
          </div>
          <div className={styles.legendItem}>
            <div className={`${styles.legendColor} ${styles.statusYellow}`}></div>
            <span>Unsure</span>
          </div>
          <div className={styles.legendItem}>
            <div className={`${styles.legendColor} ${styles.statusRed}`}></div>
            <span>Incomplete</span>
          </div>
        </div>

        {/* Tutorial grid */}
        <div className={styles.tutorialGrid}>
          {CONSTELLATION_DATA.map((constellation) => {
            const constellationSpecs = TUTORIAL_SPECS[constellation.name];
            if (!constellationSpecs) return null;

            const hiddenList = HIDDEN_TUTORIALS[constellation.name] || [];
            const visibleTutorialCount = Object.keys(constellationSpecs).length - hiddenList.length;

            return (
              <div key={constellation.name} className={styles.constellationSection}>
                <h2 className={styles.constellationName}>
                  {constellation.name} ({visibleTutorialCount})
                </h2>
                <div className={styles.tutorialList}>
                  {Object.keys(constellationSpecs)
                    .filter((indexStr) => !hiddenList.includes(parseInt(indexStr)))
                    .map((indexStr) => {
                    const index = parseInt(indexStr);
                    const title = TUTORIAL_TITLES[constellation.name]?.[index] || `Tutorial ${index + 1}`;
                    const status = getStatusColor(constellation.name, index);

                    return (
                      <div
                        key={index}
                        className={`${styles.tutorialCard} ${getStatusClassName(status)}`}
                        onClick={() => handleTutorialClick(constellation.name, index)}
                      >
                        <div className={styles.tutorialHeader}>
                          <span className={styles.tutorialNumber}>{index + 1}</span>
                          <span className={styles.tutorialTitle}>{title}</span>
                        </div>

                        <div className={styles.tutorialInfo}>
                          <div className={styles.descriptionLabel}>Learning Objective:</div>
                          <div className={styles.tutorialDescription}>
                            {getTutorialDescription(constellation.name, index)}
                          </div>

                          <div className={styles.notesSection}>
                            <div className={styles.descriptionLabel}>Dev Notes:</div>
                            <div className={styles.noteDisplay}>
                              {getTutorialNote(constellation.name, index) || (
                                <span className={styles.noteEmpty}>No notes</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation buttons */}
      <div className={styles.navButtons}>
        <Link to="/tutorials" className={styles.backButton}>
          Back to Tutorials
        </Link>
        <Link to="/pagedesign" className={styles.backButton}>
          Design System
        </Link>
      </div>
    </div>
  );
}

export default TutorialDevPage;
